import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { assetUrl } from '../assets/asset-manifest.js';

export type GameButtonSize = 'sm' | 'md' | 'lg';

/**
 * Rendered border thickness per size — decoupled from the source asset's own
 * resolution (`border-image-slice` below), so the same 543×181 face renders a
 * crisp 10–16px brass edge regardless of how large the button is
 * (`NineSlice`'s `nineSliceStyle` ties the two together, which only works for
 * the small low-res button faces it was built for).
 */
const BORDER_WIDTH_PX: Record<GameButtonSize, number> = {
  sm: 10,
  // The border, not `min-h`, is what actually sets a button's height here: two
  // 14px edges added 28px to the label's own line box, which pushed the catalog
  // card's launch control to 54px — past the height the mockup gives it — while
  // `min-h` sat unreached underneath. 11px keeps a substantial brass edge and
  // lands the control in its intended 44-50px band.
  md: 11,
  lg: 16,
};

/**
 * `md` is the launch control on a catalog card; `lg` is the page-level action
 * ("Join with Code"), which carries the Games header on its own and was
 * previously the same size as a control nested three levels down inside a
 * card. Heights grew with the refinement pass (approved Games-page mockup
 * "BUTTONS") while the horizontal padding did not: two `md` buttons sit side
 * by side inside one card at tablet widths, so extra `px` there is what forces
 * them to wrap onto two rows.
 */
/**
 * Heights are stated in whole pixels, and each size pins its own line-height,
 * so a button's box lands on the pixel grid instead of wherever the font's
 * default leading happens to fall. Left implicit, `md` rendered 48.39px — the
 * fractional part propagating into the card's height and from there into the
 * frame's edge strips, which are positioned as a percentage of it.
 */
/**
 * `sm` was `min-h-[40px]` — its border (20px) plus `leading-5` (20px) is
 * naturally exactly 40px with no `py`, so the floor was never actually
 * doing anything, and nothing here ever checked what it was a floor of.
 * 40px sits below the 44×44 CSS pixel touch target `docs/UI_GUIDELINES.md`
 * §139 and `layout.touchTarget` (`packages/design-tokens`) both call for —
 * and `sm` is a real tappable control at two live call sites
 * (`GameCatalogCard`'s "Play another" actions, `ResumeGameCard`'s
 * "Continue"), not a decorative or desktop-only size where the shortfall
 * would be moot. `Button` and `ActionLink`'s sizes are all `min-h-touch`
 * (the same 44px token) already; this brings `GameButton`/`GameActionLink`
 * into line with them rather than leaving the one decorated-face size scale
 * as the exception.
 */
const SIZE_CLASSES: Record<GameButtonSize, string> = {
  sm: 'min-h-[44px] px-3 text-sm leading-5',
  md: 'min-h-[48px] px-5 text-[1.1rem] leading-6',
  lg: 'min-h-[60px] px-7 text-[1.2rem] leading-7',
};

/**
 * The Games page's own decorated action face (docs/UI_GUIDELINES.md §8,
 * approved Games-page mockup): the shared blank green/gold 9-slice asset
 * (`ui.button.game-primary`), border-image'd behind an ordinary label —
 * brighter and more ornate than the app-wide `Button` `decorated` face, and
 * reserved for this page's launch actions (Join with Code, Host Game, Play
 * Solo, Challenge, Continue) so they read as one family.
 *
 * Only one state image was supplied — the asset itself must stay blank, with
 * every label rendered as ordinary text — so hover/active/disabled are real
 * CSS states (`brightness`/`grayscale`) over that same art rather than
 * swapped PNGs.
 */
function gameButtonStyle(size: GameButtonSize): CSSProperties {
  const width = BORDER_WIDTH_PX[size];
  return {
    borderStyle: 'solid',
    borderWidth: `${String(width)}px`,
    borderImageSource: `url("${assetUrl('uiButtonGamePrimary')}")`,
    // The face is a higher-resolution asset than the shared UI kit's button
    // faces, so its slice (source pixels) is declared independently of the
    // rendered border width above — see `BORDER_WIDTH_PX`. The slice is a
    // quarter of the 2172×724 master's 256 because the shipped face is that
    // master scaled uniformly by 1/4; the two must be rescaled together.
    borderImageSlice: '64 fill',
    borderImageWidth: `${String(width)}px`,
    borderImageOutset: '0',
    borderImageRepeat: 'stretch',
    imageRendering: 'pixelated',
  };
}

const SHARED_CLASSES =
  'interactive-lift inline-flex items-center justify-center gap-2 rounded-sm font-bold tracking-[0.01em] text-textOnDark transition-[filter] duration-fast ease-standard hover:brightness-110 active:brightness-95 disabled:pointer-events-none disabled:cursor-not-allowed disabled:grayscale disabled:opacity-60';

export interface GameButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: GameButtonSize;
  /** Shows a busy state and disables the button, without changing its layout (APP_SPEC §36). */
  isLoading?: boolean;
}

/** A real `<button>` using the Games page's decorated face — see module doc comment. */
export const GameButton = forwardRef<HTMLButtonElement, GameButtonProps>(function GameButton(
  {
    size = 'md',
    isLoading = false,
    disabled,
    className = '',
    style,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
      className={`${SHARED_CLASSES} ${SIZE_CLASSES[size]} ${className}`}
      style={{ ...gameButtonStyle(size), ...style }}
      {...rest}
    >
      {isLoading && (
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});

export interface GameActionLinkProps {
  to: string;
  size?: GameButtonSize;
  className?: string;
  children: ReactNode;
}

/** A real navigation link (e.g. "Continue") using the same decorated face as `GameButton`. */
export function GameActionLink({ to, size = 'md', className = '', children }: GameActionLinkProps) {
  return (
    <Link
      to={to}
      className={`${SHARED_CLASSES} ${SIZE_CLASSES[size]} ${className}`}
      style={gameButtonStyle(size)}
    >
      {children}
    </Link>
  );
}
